import { ReactiveParam, reactiveParam } from '@/utils/reactive-param'
import { UseQueryOptions, useQuery } from '@/composables/useQuery'
import { computed, unref } from 'vue'

/**
 * Create a query param from a list of predefined items.
 */
export function createListQuery<
    T extends readonly { value: any; label: string }[]
>(
    key: string,
    items: ReactiveParam<T>,
    options: Partial<UseQueryOptions<Values<T>>> = {}
) {
    const list = reactiveParam(items)

    const values = computed(() => {
        return unref(list).map(item => item.value)
    })

    const defaultValue = computed(() => {
        return unref(values)[0]
    })

    return [
        useQuery<Values<T>>(key, {
            validator: (v): v is Values<T> => {
                return unref(values).includes(v)
            },
            defaultValue,
            ...options
        }),
        list
    ] as const
}

/**
 * Create a query param from a list of predefined items
 * with multiple values.
 */
export function createMultipleListQuery<T extends any[]>(
    key: string,
    items: ReactiveParam<T>,
    options: Partial<UseQueryOptions<Values<T>[]>> = {}
) {
    const list = reactiveParam(items)

    const values = computed(() => {
        return unref(list)?.map(item => item.value) ?? []
    })

    const defaultValue = computed(() => {
        return unref(values)
    })

    return [
        createArrayQuery<Values<T>[]>(key, {
            validator: (v): v is Values<T>[] => {
                return Boolean(v?.some(value => unref(values).includes(value)))
            },
            normalize: v => {
                return v?.filter(value => unref(values).includes(value))
            },
            defaultValue,
            ...options
        }),
        list
    ] as const
}

/**
 * Create a boolean query param.
 */
export function createBooleanQuery(
    key: string,
    options: Partial<UseQueryOptions<boolean>> = {}
) {
    return useQuery<boolean>(key, {
        get: value => value === 'true',
        set: value => value?.toString(),
        validator: (v): v is boolean => {
            return v === true
        },
        defaultValue: false,
        ...options
    })
}

/**
 * Create an array query param.
 */
export function createArrayQuery<T extends unknown[] = string[]>(
    key: string,
    options: Partial<UseQueryOptions<T>> = {}
) {
    return useQuery<T | undefined>(key, {
        get: value => {
            return value?.split('-') as unknown as T
        },
        set: value => value?.join('-'),
        defaultValue: [] as unknown as T,
        ...options
    })
}

/**
 * Create a number query param.
 */
export function createNumberQuery(
    key: string,
    options: Partial<UseQueryOptions<number | undefined>> = {}
) {
    return useQuery<number | undefined>(key, {
        normalize: value => (!value ? undefined : Number(value)),
        validator: (v): v is number | undefined => {
            return v === undefined || Number.isInteger(v)
        },
        ...options
    })
}

/**
 * Whether the given array contains all the given values.
 */
export function hasAll(array: any[], values: any[]) {
    return values.every(value => array.includes(value))
}

/**
 * Uppercase a string or an array of strings.
 */
export function toUpperCase<T extends string | string[]>(value?: T): T {
    return (
        Array.isArray(value)
            ? value.map(toUpperCase).filter(Boolean)
            : value?.toString().toUpperCase()
    ) as T
}

type Values<T extends readonly any[]> = T[number]['value']
